import {createTikTokStyleCaptions} from '@remotion/captions';
import type {EditorState} from '../../vendor/src/features/remotion-editor/editor/state/types';
import type {CaptionsItem} from '../../vendor/src/features/remotion-editor/editor/items/captions/captions-item-type';
import type {BrandMediaBox} from '../../protocol/brand-catalog';
import {getCaptionTokenDisplayText} from '../../vendor/src/shared/lib/captions/caption-token-display-text';
import {splitCaptionsByGap,CAPTION_GAP_SPLIT_THRESHOLD_MS} from '../../vendor/src/features/remotion-editor/editor/items/captions/split-captions-by-gap';
import {calculateCaptionPageTimingFrames} from '../../vendor/src/features/remotion-editor/editor/items/captions/caption-page-sync-lead';
import {getPositionOffsetAt,type PositionKeyframe} from '../../vendor/src/features/remotion-editor/editor/items/animation/position-keyframes';
import {captionEdgeFrames} from './caption-edge-timing';
import {sourceDisplay} from './source-caption-presentation';

type Window={from:number;to:number;rows:number;height:number;item:CaptionsItem;key:string;position:{keys:PositionKeyframe[];cursor:number}};
/** Read the same page windows and source wrapping as CaptionsLayer. No caption edits. */
function captionWindows(state:EditorState,trackId:string):Window[]{
 const doc=state.undoableState,track=doc.tracks.find(t=>t.id===trackId);
 if(!track||track.hidden)throw Error('화면에 보이는 자막 트랙을 선택해 주세요.');
 const windows:Window[]=[];
 for(const id of track.items){
  const item=doc.items[id];if(item?.type!=='captions')throw Error('자막만 있는 트랙을 선택해 주세요.');
  if(!item.sourceTypography||item.groupId||item.cinematicMotion||item.cinematicMasks||item.captionPresentationNextItemId||item.rotation||item.animations?.length)throw Error('앞단에서 가져온 개별 자막 트랙을 선택하거나 카드 위치를 직접 조정해 주세요.');
  if(doc.tracks.filter(t=>t.items.includes(id)).length!==1)throw Error('자막 트랙 연결을 확인해 주세요.');
  if(item.opacity===0)continue;
  const asset=doc.assets[item.assetId];if(asset?.type!=='caption')throw Error('자막 원본 연결을 확인해 주세요.');
  const pages=splitCaptionsByGap(asset.captions,CAPTION_GAP_SPLIT_THRESHOLD_MS).flatMap(captions=>createTikTokStyleCaptions({captions,combineTokensWithinMilliseconds:item.pageDurationInMilliseconds}).pages);
  const lastWord=asset.captions.reduce((n,c)=>Number.isFinite(c.endMs)?Math.max(n,c.endMs):n,0);
  const explicit=[item.renderMeta?.videoEnd,asset.videoEnd].filter((v):v is number=>typeof v==='number'&&Number.isFinite(v)&&v>=lastWord);
  const displayEndMs=explicit.length?Math.max(...explicit):Math.max(lastWord,(item.from+item.durationInFrames)/doc.fps*1000);
  const position={keys:[...(item.positionKeyframes??[])].sort((a,b)=>a.frame-b.frame),cursor:0};
  let previousPageEndMs=0;
  pages.forEach((page,index)=>{
   const timing=captionEdgeFrames(calculateCaptionPageTimingFrames({page,nextPage:pages[index+1]??null,fps:doc.fps,captionOffsetInSeconds:item.captionStartInSeconds,previousPageEndMs,maxSyncLeadMs:item.captionSyncLeadMs??0,displayEndMs}),index,pages.length,item.durationInFrames,doc.fps,item.captionEdgeHold);
   previousPageEndMs=Math.max(previousPageEndMs,page.startMs+page.durationMs);
   const text=page.tokens.map((_,i)=>getCaptionTokenDisplayText(page.tokens,i)).join('');
   if(!text.trim())return;
   const rows=sourceDisplay(text,item.width,item.fontSize,item.sourceTypography!).split('\n').length;
   const from=Math.max(item.from,Math.ceil(item.from+timing.startFrame));
   const to=Math.min(item.from+item.durationInFrames,Math.ceil(item.from+timing.startFrame+timing.durationInFrames));
   if(to>from)windows.push({from,to,rows,height:rows*item.fontSize*item.lineHeight+2*(item.sourceTypography!.bar?.paddingY??0),item,key:`${item.assetId}:${page.startMs}`,position});
  });
 }
 if(windows.length>10000)throw Error('자막이 많습니다. 트랙을 나눠 적용하거나 카드 위치를 직접 조정해 주세요.');
 return windows;
}
/** Capture native caption presentation in editable position keys; reapply after later text/timing edits. */
export function brandCaptionCardPositions(state:EditorState,box:BrandMediaBox,trackId:string,from:number,duration:number,titleExtra:number):number[]{
 const flow=box.flow;if(!flow)throw Error('이 브랜드에는 자막을 따르는 카드 배치가 없습니다.');
 if(!Number.isInteger(duration)||duration<=0||duration>36000)throw Error('긴 클립은 나눠 적용하거나 카드 위치를 직접 조정해 주세요.');
 const all=captionWindows(state,trackId),inside=all.filter(w=>w.from<from+duration&&w.to>from);
 if(!inside.length)throw Error('선택한 클립 구간에 표시되는 자막이 없습니다. 다른 트랙을 선택하거나 고정 위치로 적용해 주세요.');
 const rowTotal=new Map<string,number>();
 for(const w of flow.pinGlobal?all:inside)rowTotal.set(w.key,Math.max(rowTotal.get(w.key)??0,w.rows));
 const total=[...rowTotal.values()].reduce((a,b)=>a+b,0);
 const cap=Math.max(1,Math.floor((flow.frameHeight-flow.startY-titleExtra-flow.gap-box.height)/flow.lineGap));
 const rawPin=flow.pinAfterLines===null?0:Math.max(0,Math.min(flow.pinAfterLines,cap));
 const pin=flow.pinGlobal&&rawPin>0?Math.min(total,cap):Math.min(rawPin,total);
 const minimum=flow.startY+flow.gap+titleExtra+pin*flow.lineGap;
 const events=new Map<number,{add:Window[];remove:Window[]}>();
 for(const w of inside){
  for(const [f,key] of [[Math.max(0,w.from-from),'add'],[Math.min(duration,w.to-from),'remove']] as const){
   const event=events.get(f)??{add:[],remove:[]};event[key].push(w);events.set(f,event);
  }
 }
 const active=new Set<Window>(),positions:number[]=[];let work=0;
 for(let f=0;f<duration;f++){
  const event=events.get(f);event?.remove.forEach(w=>active.delete(w));event?.add.forEach(w=>active.add(w));
  let top=minimum;
  for(const w of active){
   if(++work>2000000)throw Error('겹치는 자막이 많습니다. 트랙을 나누거나 카드 위치를 직접 조정해 주세요.');
   const localFrame=from+f-w.item.from,position=w.position;
   while(position.cursor<position.keys.length-1&&position.keys[position.cursor]!.frame<localFrame)position.cursor++;
   const pair=position.keys.slice(Math.max(0,position.cursor-1),position.cursor+1);
   const offset=getPositionOffsetAt(pair,localFrame).dy;
   top=Math.max(top,w.item.top+offset+w.height+flow.gap);
  }
  if(!Number.isFinite(top)||Math.abs(top)>100000)throw Error('자막 배치 수치를 확인해 주세요.');
  positions.push(top);
 }
 return positions;
}
/** Keep every integer-frame position while removing only collinear intermediate samples. */
export function brandPositionKeys(tops:readonly number[]):PositionKeyframe[]{
 if(!tops.length||tops.every(y=>Math.abs(y-tops[0]!)<1e-7))return [];
 const keys:PositionKeyframe[]=[];
 for(let f=0;f<tops.length;f++){
  const next={frame:f,dx:0,dy:tops[f]!-tops[0]!};
  while(keys.length>=2){
   const a=keys[keys.length-2]!,b=keys[keys.length-1]!;
   if(Math.abs((b.dy-a.dy)/(b.frame-a.frame)-(next.dy-b.dy)/(next.frame-b.frame))>1e-7)break;
   keys.pop();
  }
  keys.push(next);
 }
 return keys;
}
